import type { BookingFor } from "./enums.ts";

/**
 * The waiting list times, in a module with no value imports, so the schemas of the business settings
 * and of the bookings can both read them without an import cycle. `schemas/booking.ts` exports them.
 */

/**
 * Waitlist offers (B2) are for businesses where clients book for themselves (studios). A family
 * business books every free place for the first person in line at once, and its list closes
 * `WAITLIST_CUTOFF_HOURS` before the start, as before (decided 2026-10-18; strategy repo decision D41).
 */
export function waitlistOffersOn(bookingFor: BookingFor): boolean {
  return bookingFor === "self";
}

/** The waiting list of a family business closes this many hours before the session starts (decided 2026-10-18). */
export const WAITLIST_CUTOFF_HOURS = 6;

/**
 * Where late places come as offers, the list stays open until this many hours before the start, and
 * an offer always ends by then.
 */
export const WAITLIST_OFFER_CUTOFF_HOURS = 1;

/** When the waiting list of a session of this business closes, in hours before the start. */
export function waitlistCutoffHours(bookingFor: BookingFor): number {
  return waitlistOffersOn(bookingFor) ? WAITLIST_OFFER_CUTOFF_HOURS : WAITLIST_CUTOFF_HOURS;
}

/**
 * Waitlist offers (B2, studios only). A place that opens more than `waitlistLateHours` before the
 * start is booked for the first person in line, as before. A place that opens later (a late cancel)
 * goes to the first person as an offer: they accept it in `waitlistAcceptMinutes`, never after
 * `WAITLIST_OFFER_CUTOFF_HOURS` before the start, or the offer goes to the next person. The business
 * sets both on the Packages and rules page (`businesses.settings`).
 */
export const WAITLIST_LATE_HOURS_DEFAULT = 12;
export const WAITLIST_LATE_HOURS_MIN = 2;
export const WAITLIST_LATE_HOURS_MAX = 72;
export const WAITLIST_ACCEPT_MINUTES_DEFAULT = 120;
export const WAITLIST_ACCEPT_MINUTES_MIN = 15;
export const WAITLIST_ACCEPT_MINUTES_MAX = 720;

/** When an offer made at `now` ends: the accept time, but never after the list closes. */
export function waitlistOfferExpiresAt(now: Date, startsAt: Date, acceptMinutes: number): Date {
  return new Date(Math.min(now.getTime() + acceptMinutes * 60_000, startsAt.getTime() - WAITLIST_OFFER_CUTOFF_HOURS * 3_600_000));
}

/** The choices on the Packages and rules page. A value set another way is also shown there. */
export const WAITLIST_LATE_HOURS_CHOICES = [2, 4, 6, 12, 24, 48, 72] as const;
export const WAITLIST_ACCEPT_MINUTES_CHOICES = [15, 30, 60, 120, 180, 240, 360, 720] as const;

/** "30 minutes", "1 hour", "1 hour 30 minutes", "2 hours". */
export function acceptTimeWords(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hourPart = hours === 0 ? "" : hours === 1 ? "1 hour" : `${hours} hours`;
  const minutePart = rest === 0 ? "" : rest === 1 ? "1 minute" : `${rest} minutes`;
  return [hourPart, minutePart].filter(Boolean).join(" ") || "0 minutes";
}
